import { ReactNode, useCallback, useMemo, useRef } from 'react'
import { AnimationState, animationStateContext } from './useAnimationState'
import { useEvent } from 'hooks/useEvent'

export interface AnimationStateProviderProps
  extends Omit<
    AnimationState,
    'createAnimation' | 'blockEnter' | 'blockLeave'
  > {
  children: ReactNode
  onTransitionFinished?: () => void
  onInitFinished?: () => void
  onLeavePrepareFinished?: () => void
}

const useDelegateFactory = (handler: () => void) => {
  const pendingCallbacks = useRef(new Set())
  const createAnimation = useCallback(() => {
    const newCallback = () => {}
    pendingCallbacks.current.add(newCallback)
    return () => {
      pendingCallbacks.current.delete(newCallback)

      if (pendingCallbacks.current.size === 0) {
        requestAnimationFrame(() => {
          if (pendingCallbacks.current.size === 0) {
            handler()
          }
        })
      }
    }
  }, [handler])

  return createAnimation
}

const AnimationStateProvider = ({
  visible,
  stage,
  type,
  delay,
  duration,
  children,
  onTransitionFinished,
  onInitFinished,
  onLeavePrepareFinished,
}: AnimationStateProviderProps) => {
  const onTransitionFinishedEvent = useEvent(onTransitionFinished)

  const createAnimation = useDelegateFactory(onTransitionFinishedEvent)
  const onInitFinishedEvent = useEvent(onInitFinished)
  const blockEnter = useDelegateFactory(onInitFinishedEvent)
  const onLeavePrepareFinishedEvent = useEvent(onLeavePrepareFinished)
  const blockLeave = useDelegateFactory(onLeavePrepareFinishedEvent)

  const value = useMemo(
    () => ({
      visible,
      stage,
      type,
      delay,
      duration,
      createAnimation,
      blockEnter,
      blockLeave,
    }),
    [
      blockEnter,
      blockLeave,
      createAnimation,
      delay,
      duration,
      stage,
      type,
      visible,
    ],
  )

  return (
    <animationStateContext.Provider value={value}>
      {children}
    </animationStateContext.Provider>
  )
}

export default AnimationStateProvider
